import React, { useState } from 'react';
import {
    Box, Typography, Grid, Button, CircularProgress, Switch, TextField
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import {
    CardElement,
    Elements,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';

// Use the environment variable without a hardcoded fallback
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// The actual payment form component
const StripeCardForm = ({ onSuccess, onError, onCancel, darkMode, hideTestInfo, isLoading }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [cardholderName, setCardholderName] = useState('');
    const [setAsDefault, setSetAsDefault] = useState(true);
    const [localLoading, setLocalLoading] = useState(false);
    const [cardError, setCardError] = useState(null);

    // Combined loading state (either passed from parent or local)
    const loading = isLoading || localLoading;

    // Card element styling
    const cardElementOptions = {
        style: {
            base: {
                color: darkMode ? '#fff' : '#32325d',
                fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                fontSmoothing: 'antialiased',
                fontSize: '16px',
                '::placeholder': {
                    color: darkMode ? '#aab7c4' : '#aab7c4',
                },
                backgroundColor: 'transparent',
            },
            invalid: {
                color: '#fa755a',
                iconColor: '#fa755a',
            },
        },
        hidePostalCode: true, // We'll collect this separately if needed
    };

    const handleCardChange = (event) => {
        setCardError(event.error ? event.error.message : '');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalLoading(true);
        setCardError(null);

        if (!stripe || !elements) {
            setCardError("Stripe hasn't loaded yet. Please try again.");
            setLocalLoading(false);
            return;
        }

        if (!cardholderName.trim()) {
            setCardError("Please enter the cardholder's name");
            setLocalLoading(false);
            return;
        }

        // Get the card element
        const cardElement = elements.getElement(CardElement);

        try {
            // Create payment method
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name: cardholderName,
                },
            });

            if (error) {
                setCardError(error.message);
                setLocalLoading(false);
                if (onError) onError(error.message);
                return;
            }

            // Payment method created successfully
            if (onSuccess) {
                // We don't set loading to false here because the parent component will handle it
                await onSuccess(paymentMethod.id, setAsDefault);
            } else {
                setLocalLoading(false);
            }

            // Clear form (only if parent didn't handle loading state)
            if (!isLoading) {
                cardElement.clear();
                setCardholderName('');
            }
        } catch (err) {
            console.error("Error creating payment method:", err);
            setCardError("Failed to process your card. Please try again.");
            if (onError) onError(err.message || "Payment processing failed");
            setLocalLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TextField
                        label="Cardholder Name"
                        value={cardholderName}
                        onChange={(e) => setCardholderName(e.target.value)}
                        required
                        fullWidth
                        disabled={loading}
                        variant="outlined"
                        placeholder="Name as it appears on card"
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                                borderRadius: '10px'
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                        Card Information
                    </Typography>
                    <Box
                        sx={{
                            p: 2,
                            border: `1px solid ${cardError ? '#fa755a' : darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                            borderRadius: '10px',
                            backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                            mb: 2,
                            opacity: loading ? 0.7 : 1
                        }}
                    >
                        <CardElement 
                            options={cardElementOptions} 
                            onChange={handleCardChange}
                            disabled={loading}
                        />
                    </Box>
                    {cardError && (
                        <Typography color="error" variant="caption" sx={{ display: 'block', mb: 2 }}>
                            {cardError}
                        </Typography>
                    )}
                </Grid>
                
                <Grid item xs={12}>
                    <Box display="flex" alignItems="center">
                        <Switch
                            checked={setAsDefault}
                            onChange={(e) => setSetAsDefault(e.target.checked)}
                            color="primary"
                            disabled={loading}
                        />
                        <Typography variant="body2">
                            Set as default payment method
                        </Typography>
                    </Box>
                </Grid>
                
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                            variant="outlined"
                            onClick={onCancel}
                            disabled={loading}
                            sx={{ 
                                borderRadius: '10px',
                                textTransform: 'none'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading || !stripe}
                            sx={{
                                background: 'linear-gradient(45deg, #FF4775 30%, #FF7676 90%)',
                                color: 'white',
                                borderRadius: '10px',
                                textTransform: 'none',
                                '&:hover': {
                                    boxShadow: '0 6px 20px rgba(255, 71, 117, 0.3)',
                                },
                                '&.Mui-disabled': {
                                    background: 'linear-gradient(45deg, #FF4775 30%, #FF7676 90%)',
                                    opacity: 0.7,
                                    color: 'white'
                                }
                            }}
                        >
                            {loading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                    Processing...
                                </Box>
                            ) : 'Add Card'}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </form>
    );
};

// Wrapper with Stripe Elements
const StripePaymentForm = ({ onSuccess, onCancel, onError, darkMode, hideTestInfo, isLoading }) => {
    return (
        <Elements stripe={stripePromise}>
            <StripeCardForm 
                onSuccess={onSuccess} 
                onCancel={onCancel} 
                onError={onError}
                darkMode={darkMode}
                hideTestInfo={hideTestInfo}
                isLoading={isLoading}
            />
        </Elements>
    );
};

export default StripePaymentForm;